import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { toast } from "sonner";
import { installmentEndpoint } from "@/config/endpoints";
import { invalidateLeadBoard, invalidateRecentActivityFeed } from "@/lib/queryClient";
import type {
  InstallmentFrequency,
  InstallmentPlan,
  InstallmentRounding,
  SchedulePreviewResponse,
  SettlementAllocation,
} from "../components/view-lead/components/Ongoing/installments/types";

/**
 * EMI plan reads and writes.
 *
 * The SCHEDULE itself rides the existing `/core/child?leadId=` payload (the
 * Payments tab already reads `child.payments` from there, not from
 * `/core/payment`), so there is no query key for it and `["childs"]` already
 * refreshes it. These hooks cover the writes plus the two things the child
 * payload cannot answer: a preview of a plan that does not exist yet, and the
 * template attached to a catalog item.
 */

export interface SchedulePreviewPayload {
  childId: number;
  principal?: number;
  frequency: InstallmentFrequency;
  intervalDays?: number | null;
  startDate: string;
  rounding?: InstallmentRounding;
  count?: number | null;
  amountPerInstallment?: number | null;
}

/** One row of a custom ladder (unequal amounts on chosen dates). */
export interface CustomPlanRow {
  amount: number;
  /** YYYY-MM-DD */
  dueDate: string;
}

export interface CreatePlanPayload extends SchedulePreviewPayload {
  /** Custom ladder: replaces the cadence; the plan totals these rows. */
  rows?: CustomPlanRow[];
  reminderDaysBefore: number;
  reminderHourIst?: number;
  note?: string | null;
  templateId?: number | null;
}

/**
 * Busts everything a plan write can touch.
 *
 * `["childs"]` is the load-bearing one — the Payments tab renders the schedule
 * straight off the embedded child payload, so without it a just-recorded
 * instalment shows PENDING for the full 3-minute staleTime on the very screen
 * that showed the success toast.
 */
function useInvalidateInstallmentQueries() {
  const queryClient = useQueryClient();
  return () => {
    invalidateLeadBoard();
    queryClient.invalidateQueries({ queryKey: ["childs"] });
    queryClient.invalidateQueries({ queryKey: ["get-child"] });
    queryClient.invalidateQueries({ queryKey: ["activity-log"] });
    invalidateRecentActivityFeed();
    queryClient.invalidateQueries({ queryKey: ["payments"] });
    queryClient.invalidateQueries({ queryKey: ["payment-summary"] });
    queryClient.invalidateQueries({ queryKey: ["installment-plan"] });
  };
}

/** Server-side schedule preview. Writes nothing. */
export function usePreviewSchedule() {
  return useMutation({
    mutationFn: async (payload: SchedulePreviewPayload) => {
      const res = await axiosInstance.post<{ preview: SchedulePreviewResponse }>(
        `${installmentEndpoint}/preview`,
        payload,
      );
      return res.data.preview;
    },
  });
}

export function useCreateInstallmentPlan() {
  const invalidate = useInvalidateInstallmentQueries();
  return useMutation({
    mutationFn: async (payload: CreatePlanPayload) => {
      const res = await axiosInstance.post<{ plan: InstallmentPlan }>(
        `${installmentEndpoint}/plan`,
        payload,
      );
      return res.data.plan;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Instalment plan created");
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || "Could not create the plan");
    },
  });
}

/**
 * Re-spread the UNPAID tail over the deal's current value.
 *
 * Never touches a paid or waived row — those are money that already moved. Used
 * after the deal's value changes and the plan goes out of sync.
 */
export function useRebuildInstallmentPlan() {
  const invalidate = useInvalidateInstallmentQueries();
  return useMutation({
    mutationFn: async ({
      planId,
      ...body
    }: {
      planId: number;
      /** New total for the UNPAID tail — not the plan's original principal. */
      remainingPrincipal?: number;
      count?: number;
      amountPerInstallment?: number;
      frequency?: InstallmentFrequency;
      startDate?: string;
      rounding?: InstallmentRounding;
      /** Custom rows for the unpaid tail; must add up to remainingPrincipal. */
      rows?: CustomPlanRow[];
    }) => {
      const res = await axiosInstance.post(
        `${installmentEndpoint}/plan/${planId}/rebuild`,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Remaining instalments updated");
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || "Could not update the plan");
    },
  });
}

export function useDeleteInstallmentPlan() {
  const invalidate = useInvalidateInstallmentQueries();
  return useMutation({
    mutationFn: async (planId: number) => {
      await axiosInstance.delete(`${installmentEndpoint}/plan/${planId}`);
    },
    onSuccess: () => {
      invalidate();
      toast.success("Instalment plan removed");
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || "Could not remove the plan");
    },
  });
}

/**
 * Record ONE payment against explicit instalments.
 *
 * `allocations` is the split the operator was shown before they hit save —
 * there is no server-side waterfall, by product decision. The server writes one
 * `DealPayment` row per allocation inside a single transaction and ties them
 * together with a settlement reference.
 */
export function useSettleInstallments() {
  const invalidate = useInvalidateInstallmentQueries();
  return useMutation({
    mutationFn: async (payload: {
      childId: number;
      method: string;
      paidAt?: string;
      txnId?: string;
      note?: string;
      receiptDocId?: number;
      allocations: SettlementAllocation[];
      /** Anything the schedule doesn't claim — stored, never dropped. */
      unadjustedAmount?: number;
    }) => {
      const res = await axiosInstance.post(
        `${installmentEndpoint}/settle`,
        payload,
      );
      return res.data;
    },
    onSuccess: (_d, vars) => {
      invalidate();
      const n = vars.allocations.length;
      const extra = vars.unadjustedAmount ?? 0;
      toast.success(
        extra > 0
          ? `Payment recorded · ₹${extra.toLocaleString("en-IN")} left unadjusted`
          : n > 1
            ? `Payment recorded across ${n} instalments`
            : "Payment recorded",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || "Could not record the payment");
      // Refetch on FAILURE too. Every rejection here is the server telling us
      // the ladder we rendered is not the ladder it holds — "#10 still owes
      // ₹0.00" on a row we are showing as unpaid is exactly that. Re-reading
      // makes the screen agree with the truth instead of leaving the operator
      // pressing a button that can only fail.
      invalidate();
    },
  });
}

/**
 * Put the deal's unadjusted credit onto the instalments it covers, oldest due
 * first. Re-attributes existing payment rows — the deal's total never moves.
 */
export function useApplyInstallmentCredit() {
  const invalidate = useInvalidateInstallmentQueries();
  return useMutation({
    mutationFn: async (childId: number) => {
      const res = await axiosInstance.post(
        `${installmentEndpoint}/apply-credit`,
        { childId },
      );
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Unadjusted amount applied to instalments");
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || "Could not apply the credit");
    },
  });
}

/**
 * Foreclose: settle every remaining instalment now. Credit on the deal is spent
 * before any new cash is asked for.
 */
export function useForecloseInstallmentPlan() {
  const invalidate = useInvalidateInstallmentQueries();
  return useMutation({
    mutationFn: async (payload: {
      childId: number;
      method: string;
      paidAt?: string;
      txnId?: string | null;
      note?: string | null;
      useCredit?: boolean;
      /** What the operator was shown — the server refuses if it has moved. */
      expectedAmount?: number;
    }) => {
      const res = await axiosInstance.post(
        `${installmentEndpoint}/foreclose`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Instalment plan foreclosed");
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || "Could not foreclose the plan");
    },
  });
}

/** Reverse a foreclosure — the schedule goes back to open. */
export function useReopenInstallmentPlan() {
  const invalidate = useInvalidateInstallmentQueries();
  return useMutation({
    mutationFn: async (planId: number) => {
      const res = await axiosInstance.post(
        `${installmentEndpoint}/plan/${planId}/reopen`,
      );
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Foreclosure reversed — the schedule is open again");
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || "Could not reopen the plan");
    },
  });
}

export function useWaiveInstallment() {
  const invalidate = useInvalidateInstallmentQueries();
  return useMutation({
    mutationFn: async ({
      installmentId,
      ...body
    }: {
      installmentId: number;
      amount?: number;
      reason: string;
    }) => {
      const res = await axiosInstance.post(
        `${installmentEndpoint}/${installmentId}/waive`,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      toast.success("Instalment waived");
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || "Could not waive the instalment");
    },
  });
}

/**
 * The EMI template attached to the deal's catalog item, plus a preview built
 * from the deal's LIVE money. Powers "prefilled from the programme".
 *
 * `enabled` is the caller's gate — only fetched while the plan modal is open.
 */
export function useResolveInstallmentTemplate(
  childId: number | null | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["installment-plan", "template", childId],
    enabled: !!childId && enabled,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `${installmentEndpoint}/template/resolve`,
        { params: { childId } },
      );
      return res.data;
    },
  });
}
